import { z } from 'zod';
import {
  GroupedEnum,
  IBitType,
  IBitUsedUnit,
  IJobType,
  ILaserOrEndmill,
  IMaterialUsed,
  IOnefinityController,
  IOnefinityModel,
  IOnefinitySize,
  IProbeForZHeightCommand,
  ISoftwareUsed,
  IToolSetter,
  IUnits,
  IXYDatumPosition,
  IZZeroPosition,
} from './post.d';
import { getGroupedOptionsFlat } from './post.utils';
import { isValidYoutubeUrl } from '@/lib/youtube';

export function createEnum<T extends string>(values: T[]): [T, ...T[]] {
  if (values.length === 0) {
    throw new Error('Enum values must have at least one item');
  }
  return values as [T, ...T[]];
}

export const modelUploadSchema = z.object({
  models: z
    .array(
      z.object({
        modelFile: z
          .instanceof(File, {
            message:
              'The uploaded file is invalid. Please upload a valid file.',
          })
          .refine(
            (file) => {
              const fileName = file.name.toLowerCase();
              const allowedExtensions: string[] = ['.gcode', '.ngc', '.nc'];
              return allowedExtensions.some((ext) => fileName.endsWith(ext));
            },
            {
              message:
                'Invalid file type. Only GCODE, NGC, or NC files are allowed.',
            }
          )
          .optional(),
        modelURL: z.string().optional(),
      })
    )
    .min(1, { message: 'At least one model must be uploaded.' }),
});

export const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.svg'];

export const allowedMediaExtensions = [
  '.crv',
  '.crv3d',
  '.art',
  '.c2d',
  '.f3d',
  '.ai',
  '.pdf',
  '.dxf',
  '.stl',
  ...imageExtensions,
];

export const mediaUploadSchema = z.object({
  mediaFiles: z
    .array(
      z
        .instanceof(File, {
          message: 'Each uploaded item must be a valid file.',
        })
        .refine(
          (file) => {
            const fileName = file.name.toLowerCase();
            return allowedMediaExtensions.some((ext) => fileName.endsWith(ext));
          },
          {
            message: `Invalid file type. Only Images, Vectors, Documents are allowed.`,
          }
        ),
      { message: 'At least one media file is required.' }
    )
    .min(1, { message: 'At least one media file is required.' })
    .optional(),
  youtubeAttachments: z
    .array(
      z
        .string()
        .url({ message: 'Must be a valid URL' })
        .refine(isValidYoutubeUrl, {
          message: 'Must be a valid youtube shorts URL',
        })
    )
    .refine((urls) => new Set(urls).size === urls.length, {
      message: 'YouTube URL cant be added twice.',
    })
    .optional()
    .default([]),
});

export const postUploadSchema = z.object({
  title: z.string().min(3, {
    message: 'Title must be at least 3 characters long',
  }),
  description: z.string().min(3, {
    message: 'Description must be at least 3 characters long',
  }),
  category: z.string().min(2, {
    message: 'Category must be selected',
  }),
  tags: z.array(z.string()).min(2, {
    message: 'At least two tags are required',
  }),
});

export const onefinityModelSchema = z.object({
  onefinityModel: z
    .enum(createEnum(Object.values(IOnefinityModel)))
    .refine((val) => !!val, {
      message: 'Please select a valid Onefinity model.',
    }),
  onefinityController: z
    .enum(createEnum(Object.values(IOnefinityController)))
    .refine((val) => !!val, { message: 'Please select a valid controller.' }),
  onefinitySize: z
    .enum(createEnum(Object.values(IOnefinitySize)))
    .refine((val) => !!val, {
      message: 'Please select a valid Onefinity size.',
    }),
});

export const licenceDetailsSchema = z.object({
  licenceType: z.string(),
});

export const modelToolMappingSchema = z
  .object({
    selectedModel: z.number(),
    laserOrEndmill: z.nativeEnum(ILaserOrEndmill, {
      required_error: 'Tool type is required',
    }),
  })
  .and(
    z.discriminatedUnion('laserOrEndmill', [
      z.object({
        laserOrEndmill: z.literal(ILaserOrEndmill.LASER),
      }),
      z.object({
        laserOrEndmill: z.literal(ILaserOrEndmill.ENDMILL),
        toolUnit: z.nativeEnum(IBitUsedUnit, {
          required_error: 'Tool unit is required',
        }),
        toolUsed: z.number({
          required_error: 'Tool size is required',
          invalid_type_error: 'Tool size must be a number',
        }),
        toolType: z.nativeEnum(IBitType, {
          required_error: 'Tool type is required',
        }),
      }),
    ])
  )
  .refine(
    (data) => {
      if (
        data.laserOrEndmill === ILaserOrEndmill.ENDMILL &&
        data.toolUnit === IBitUsedUnit.INCHES
      ) {
        return data.toolUsed >= 0.01 && data.toolUsed <= 10;
      }
      if (
        data.laserOrEndmill === ILaserOrEndmill.ENDMILL &&
        data.toolUnit === IBitUsedUnit.MM
      ) {
        return data.toolUsed >= 0.01 && data.toolUsed <= 1000;
      }
      return true;
    },
    {
      message: 'Invalid tool size for the selected unit',
      path: ['toolUsed'],
    }
  );

export const modelToolMappingPageSchema = z
  .object({
    models: z.array(z.any()),
    toolData: z.array(z.any()),
  })
  .superRefine((data, ctx) => {
    const modelIdList = data.models.map((model) => model.id);
    const modelIdInToolDataList = data.toolData.map((tool) => tool.modelId);

    if (!modelIdList.every((id) => modelIdInToolDataList.includes(id))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['selectedModel'],
        message:
          'All models must have a tool assigned, switch models here to add tools',
      });
    }
  });

export const jobSetupModelSchema = z
  .object({
    softwareUsed: z
      .enum(createEnum(Object.values(ISoftwareUsed)))
      .refine((val) => !!val, { message: 'Please select the software used.' }),
    materialUsed: z
      .enum(
        createEnum(
          getGroupedOptionsFlat(IMaterialUsed as unknown as GroupedEnum)
        )
      )
      .refine((val) => !!val, { message: 'Please select the material used.' }),
    jobType: z
      .enum(createEnum(Object.values(IJobType)))
      .refine((val) => !!val, { message: 'Please select a job type.' }),
    units: z
      .enum(createEnum(Object.values(IUnits)))
      .refine((val) => !!val, { message: 'Please select the units.' }),
    zZeroPosition: z
      .enum(createEnum(Object.values(IZZeroPosition)))
      .refine((val) => !!val, {
        message: 'Please select the Z zero position.',
      }),
    xyDatumPosition: z
      .enum(createEnum(Object.values(IXYDatumPosition)))
      .refine((val) => !!val, {
        message: 'Please select the XY datum position.',
      }),
    toolSetter: z
      .enum(createEnum(Object.values(IToolSetter)))
      .refine((val) => !!val, { message: 'Please select the tool setter.' }),
    probeForZHeightCommand: z
      .enum(createEnum(Object.values(IProbeForZHeightCommand)))
      .refine((val) => !!val, {
        message: 'Please select the probe for Z height command.',
      }),
    spindleSpeed: z
      .number()
      .min(6000, { message: 'Spindle speed must be at least 6000 RPM.' })
      .max(24000, { message: 'Spindle speed must be at most 24000 RPM.' })
      .refine((val) => val > 0, {
        message: 'Please enter a valid spindle speed.',
      }),
  })
  .and(
    z.discriminatedUnion('jobType', [
      z.object({
        jobType: z.literal(IJobType.ROTARY),
        jobSizeDiameter: z
          .number()
          .positive('Job diameter must be a positive number.'),
        jobSizeLength: z
          .number()
          .positive('Job length must be a positive number.'),
      }),
      z.object({
        jobType: z.enum([IJobType.SINGLE_SIDED, IJobType.DOUBLE_SIDED]),
        jobSizeWidth: z
          .number()
          .positive('Job width must be a positive number.'),
        jobSizeHeight: z
          .number()
          .positive('Job height must be a positive number.'),
        jobSizeThickness: z
          .number()
          .positive('Job thickness must be a positive number.'),
      }),
    ])
  );
